import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Calendar, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EquipmentRentalSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('loading');
  const [rental, setRental] = useState(null);
  const maxAttempts = 15;

  const checkStatus = useCallback(
    async (attempt = 0) => {
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/api/equipment/rentals/status/${sessionId}`);
        setRental(response.data.rental);

        if (response.data.payment_status === 'paid') {
          setStatus('complete');
        } else {
          setStatus('pending');
          setTimeout(() => checkStatus(attempt + 1), 2000);
        }
      } catch (error) {
        setStatus('error');
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (sessionId) {
      checkStatus();
    }
  }, [sessionId, checkStatus]);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  if (status === 'loading' || status === 'pending') {
    return (
      <div className="min-h-screen pt-20 bg-[#050814] flex items-center justify-center">
        <Card className="max-w-md w-full bg-[#0B132B] border border-[#B8860B]/30">
          <CardContent className="p-12 text-center">
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-[#B8860B] animate-spin" />
            <h2 className="text-2xl font-bold text-[#F4F1EA] mb-2">Processing Your Rental...</h2>
            <p className="text-[#F4F1EA]/70">Please wait while we confirm your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'timeout') {
    return (
      <div className="min-h-screen pt-20 bg-[#050814] flex items-center justify-center">
        <Card className="max-w-md w-full bg-[#0B132B] border border-[#B8860B]/30">
          <CardContent className="p-12 text-center">
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-[#B8860B]" />
            <h2 className="text-2xl font-bold text-[#F4F1EA] mb-2">Payment received — finalizing</h2>
            <p className="text-[#F4F1EA]/80 mb-2">
              Stripe is taking a little longer than usual. Your card has been charged successfully.
            </p>
            <p className="text-[#F4F1EA]/70 mb-6">
              Refresh in a moment to see your confirmation, or check your email.
            </p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA] font-semibold"
            >
              Refresh Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen pt-20 bg-[#050814] flex items-center justify-center">
        <Card className="max-w-md w-full bg-[#0B132B] border border-[#B8860B]/30">
          <CardContent className="p-12 text-center">
            <h2 className="text-2xl font-bold text-red-500 mb-4">Something Went Wrong</h2>
            <p className="text-[#F4F1EA]/70 mb-6">We couldn't verify your rental. Please contact us.</p>
            <Link to="/contact">
              <Button className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]">Contact Us</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#050814]">
      <div className="container mx-auto px-4 py-12">
        <Card
          data-testid="rental-success-card"
          className="max-w-3xl mx-auto shadow-2xl bg-[#0B132B] border border-[#B8860B]/30"
        >
          <CardContent className="p-12">
            <div className="text-center mb-8">
              <CheckCircle className="w-20 h-20 mx-auto mb-4 text-[#B8860B]" />
              <h1 className="text-4xl font-bold text-[#F4F1EA] mb-2">Rental Confirmed!</h1>
              <p className="text-lg text-[#F4F1EA]/70">
                Your equipment rental has been successfully booked.
              </p>
            </div>

            {rental && (
              <div className="bg-[#050814] border border-[#B8860B]/20 rounded-lg p-6 mb-8 space-y-4">
                <div className="flex justify-between border-b border-[#B8860B]/20 pb-3">
                  <span className="font-semibold text-[#F4F1EA]">Confirmation Number</span>
                  <span className="text-[#B8860B] font-bold">#{rental.id.slice(0, 8).toUpperCase()}</span>
                </div>

                <div className="flex justify-between border-b border-[#B8860B]/20 pb-3">
                  <span className="font-semibold text-[#F4F1EA]">Equipment</span>
                  <span className="text-[#F4F1EA]/80">{rental.equipment_name}</span>
                </div>

                {rental.tractor_attachment_name && (
                  <div className="flex justify-between border-b border-[#B8860B]/20 pb-3">
                    <span className="font-semibold text-[#F4F1EA]">Attachment</span>
                    <span className="text-[#F4F1EA]/80">{rental.tractor_attachment_name}</span>
                  </div>
                )}

                <div className="flex justify-between border-b border-[#B8860B]/20 pb-3">
                  <span className="font-semibold text-[#F4F1EA]">Rental Period</span>
                  <span className="text-[#F4F1EA]/80">{rental.rental_days} days</span>
                </div>

                <div className="flex justify-between border-b border-[#B8860B]/20 pb-3">
                  <span className="font-semibold text-[#F4F1EA]">Start Date</span>
                  <span className="text-[#F4F1EA]/80">{formatDate(rental.start_date)}</span>
                </div>

                <div className="flex justify-between border-b border-[#B8860B]/20 pb-3">
                  <span className="font-semibold text-[#F4F1EA]">End Date</span>
                  <span className="text-[#F4F1EA]/80">{formatDate(rental.end_date)}</span>
                </div>

                <div className="flex justify-between border-b border-[#B8860B]/20 pb-3">
                  <span className="font-semibold text-[#F4F1EA]">Rental Amount</span>
                  <span className="text-[#F4F1EA]/80">${rental.rental_amount.toFixed(2)}</span>
                </div>

                <div className="flex justify-between border-b border-[#B8860B]/20 pb-3">
                  <span className="font-semibold text-[#F4F1EA]">Deposit</span>
                  <span className="text-[#F4F1EA]/80">${rental.deposit_amount.toFixed(2)}</span>
                </div>

                <div className="flex justify-between pt-2">
                  <span className="text-lg font-bold text-[#F4F1EA]">Total Paid</span>
                  <span className="text-2xl font-bold text-[#B8860B]">${rental.total_amount.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="bg-[#050814] border-2 border-[#B8860B] rounded-lg p-6 mb-8">
              <h3 className="font-bold text-[#B8860B] text-lg mb-4 flex items-center">
                <Calendar className="mr-2 text-[#B8860B]" /> What's Next?
              </h3>
              <ul className="space-y-3 text-[#F4F1EA]/80">
                <li className="flex items-start">
                  <Mail className="mr-2 h-5 w-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
                  <span>A confirmation email has been sent to <strong className="text-[#F4F1EA]">{rental?.email}</strong></span>
                </li>
                <li className="flex items-start">
                  <Phone className="mr-2 h-5 w-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
                  <span>You'll receive SMS updates about your rental</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
                  <span>Equipment will be ready for pickup on your start date</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-[#B8860B] flex-shrink-0 mt-0.5" />
                  <span>Deposit will be refunded upon equipment return in good condition</span>
                </li>
              </ul>
            </div>

            <div className="text-center space-y-4">
              <p className="text-[#F4F1EA]/70">Questions about your rental?</p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#050814]"
                  >
                    <Mail className="mr-2 h-4 w-4" /> Contact Us
                  </Button>
                </Link>
                <a href="tel:8327140101">
                  <Button
                    variant="outline"
                    className="border-[#F4F1EA]/30 text-[#F4F1EA] hover:bg-[#F4F1EA]/10"
                  >
                    <Phone className="mr-2 h-4 w-4" /> (832) 714-0101
                  </Button>
                </a>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link to="/">
                <Button className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA] mr-4">
                  Back to Home
                </Button>
              </Link>
              <Link to="/equipment">
                <Button className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#050814] font-semibold">
                  Browse More Equipment
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
